import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Flame } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Button, useToast } from '../components/ui';
import { connectSocket } from '../utils/socket';

// Map spice level → label + color class for KDS chips
const SPICE_DISPLAY = {
  mild:      { label: 'Mild',      classes: 'bg-success-100 text-success-700 border-success-200', flame: 'text-success-600' },
  medium:    { label: 'Medium',    classes: 'bg-warning-100 text-warning-700 border-warning-200', flame: 'text-warning-600' },
  hot:       { label: 'Hot',       classes: 'bg-danger-100 text-danger-700 border-danger-200',     flame: 'text-danger-600' },
  extra_hot: { label: 'Extra hot', classes: 'bg-danger-100 text-danger-700 border-danger-300 font-bold', flame: 'text-danger-700' },
};

// Visual config per column — keeps colors consistent across header + accents
const COLUMN_STYLES = {
  pending:   { label: 'Pending',   border: 'border-t-neutral-400', dot: 'bg-neutral-400', tint: 'text-neutral-600' },
  preparing: { label: 'Preparing', border: 'border-t-warning-500', dot: 'bg-warning-500', tint: 'text-warning-700' },
  ready:     { label: 'Ready',     border: 'border-t-primary-500', dot: 'bg-primary-500', tint: 'text-primary-700' },
  served:    { label: 'Served',    border: 'border-t-success-500', dot: 'bg-success-500', tint: 'text-success-700' },
};

const KDS = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [filterWaiter, setFilterWaiter] = useState('all');

  useEffect(() => {
    // Live updates from other screens (waiter placing orders, cashier
    // completing them). This screen's OWN clicks update state directly
    // from the API response — the socket is a bonus, not a dependency.
    const newSocket = connectSocket();
    newSocket.emit('join-kitchen');

    newSocket.on('new-order', (order) => {
      setOrders(prev => prev.some(o => o._id === order._id) ? prev : [order, ...prev]);
    });

    newSocket.on('order-updated', (order) => {
      setOrders(prev => prev.map(o => o._id === order._id ? order : o));
    });

    fetchOrders();

    return () => {
      if (newSocket) newSocket.close();
    };
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get('/api/kds/orders', { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setOrders(res.data);
    } catch (err) { console.error(err); }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await axios.put(`/api/kds/order/${id}`, { status }, { headers: { 'x-auth-token': localStorage.getItem('token') } });
      // Move the card immediately from the API response — no reload, no
      // reliance on the socket round-trip.
      setOrders(prev => prev.map(o => o._id === res.data._id ? res.data : o));
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Could not update the order');
    }
  };

  const hasFullKDSAccess = ['chef', 'super_admin', 'admin', 'manager'].includes(user?.role);

  const displayedOrders = (user?.role === 'waiter' || user?.role === 'cashier') && filterWaiter !== 'all'
    ? orders.filter(o => o.waiter?._id === filterWaiter || o.servedBy?._id === filterWaiter)
    : orders;

  // Short, readable order number — last 6 hex chars from "ORD<timestamp><hex>"
  const shortOrderNumber = (n) => {
    if (!n) return '';
    return '#' + String(n).slice(-6);
  };

  return (
    <>
      <PageHeader
        title="Kitchen Display"
        subtitle="Tickets flow here from POS. Tap to advance through prep stages."
      />

      {/* Waiter/Cashier Filter */}
      {(user?.role === 'waiter' || user?.role === 'cashier') && (
        <div className="mb-4">
          <select
            value={filterWaiter}
            onChange={(e) => setFilterWaiter(e.target.value)}
            className="border border-neutral-300 px-3 py-2 rounded-md text-sm bg-white focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
          >
            <option value="all">All Orders</option>
            <option value={user?._id}>My Orders</option>
          </select>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {['pending', 'preparing', 'ready', 'served'].map(status => {
          const style = COLUMN_STYLES[status];
          const inColumn = displayedOrders.filter(o => o.status === status);

          return (
            <div
              key={status}
              className={`bg-white rounded-lg border border-neutral-200 border-t-4 ${style.border} shadow-card overflow-hidden`}
            >
              {/* Column header */}
              <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between bg-neutral-50/50">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                  <h2 className={`text-sm font-semibold uppercase tracking-wider ${style.tint}`}>
                    {style.label}
                  </h2>
                </div>
                <span className="text-xs font-bold bg-white border border-neutral-200 text-neutral-700 px-2 py-0.5 rounded-full">
                  {inColumn.length}
                </span>
              </div>

              {/* Cards */}
              <div className="p-3 space-y-3 min-h-[200px]">
                {inColumn.length === 0 && (
                  <p className="text-center text-xs text-neutral-400 py-8 italic">No tickets</p>
                )}

                {inColumn.map(order => (
                  <div
                    key={order._id}
                    className="bg-white border border-neutral-200 rounded-md p-3 shadow-card hover:shadow-card-hover transition-shadow"
                  >
                    {/* Order header */}
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-mono text-sm font-bold text-neutral-900">
                        {shortOrderNumber(order.orderNumber)}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded">
                        {order.orderType}
                      </span>
                    </div>

                    {/* Table / waiter info */}
                    <div className="text-xs text-neutral-500 mb-3 leading-tight">
                      {order.tableNumber && (
                        <span className="font-medium text-neutral-700">Table #{order.tableNumber} · </span>
                      )}
                      <span>by {order.waiter?.name || 'Unknown'}</span>
                      {order.servedBy && (
                        <span> → served by {order.servedBy.name}</span>
                      )}
                    </div>

                    {/* Items */}
                    <ul className="space-y-1.5 mb-3 border-t border-neutral-100 pt-2">
                      {order.items?.map((item, idx) => {
                        const spice = SPICE_DISPLAY[item.spiceLevel];
                        return (
                          <li key={idx} className="flex items-start gap-2 text-sm text-neutral-800">
                            <span className="inline-flex flex-shrink-0 w-5 h-5 rounded bg-neutral-100 text-neutral-700 text-xs font-semibold items-center justify-center">
                              {item.quantity}
                            </span>
                            <div className="leading-tight flex-1 min-w-0">
                              <p>{item.menuItem?.name || 'Item'}</p>
                              {spice && (
                                <span className={`inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-wider ${spice.classes}`}>
                                  <Flame size={10} className={spice.flame} />
                                  {spice.label}
                                </span>
                              )}
                              {(item.selectedAddOns || []).length > 0 && (
                                <ul className="mt-0.5 ml-1 text-[11px] text-primary-700 space-y-0.5">
                                  {item.selectedAddOns.map((a, ai) => (
                                    <li key={ai}>+ {a.name}</li>
                                  ))}
                                </ul>
                              )}
                              {item.notes && (
                                <p className="text-[10px] text-neutral-500 italic mt-0.5">{item.notes}</p>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>

                    {/* Actions */}
                    <div className="pt-1">
                      {status === 'pending' && hasFullKDSAccess && (
                        <Button
                          variant="primary"
                          size="sm"
                          fullWidth
                          onClick={() => updateStatus(order._id, 'preparing')}
                        >
                          Start preparing
                        </Button>
                      )}
                      {status === 'preparing' && hasFullKDSAccess && (
                        <Button
                          variant="success"
                          size="sm"
                          fullWidth
                          onClick={() => updateStatus(order._id, 'ready')}
                        >
                          Mark ready
                        </Button>
                      )}
                      {status === 'ready' && order.orderType === 'dine-in' && (
                        <p className="text-xs text-primary-700 bg-primary-50 border border-primary-100 rounded px-2 py-1.5 text-center">
                          Ready — waiter will serve
                        </p>
                      )}
                      {status === 'ready' && (order.orderType === 'takeaway' || order.orderType === 'drive-thru') && (
                        <p className="text-xs text-neutral-600 bg-neutral-100 rounded px-2 py-1.5 text-center">
                          Ready for pickup — pay at counter
                        </p>
                      )}
                      {status === 'ready' && order.orderType === 'delivery' && hasFullKDSAccess && (
                        <Button
                          variant="primary"
                          size="sm"
                          fullWidth
                          onClick={() => updateStatus(order._id, 'served')}
                        >
                          Hand off to rider
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default KDS;
